import type {
  CreateCategoryPayload,
  CreateTagPayload,
  InsightTagCategory,
  InsightTagsCatalogue,
  InsightTagsSummary,
  RenameTagPayload,
  UpdateCategoryPayload
} from './types';
import { defaultInsightTagCategories } from './fixtures';

/** Bump when fixture seed shape/colours change so local sessionStorage reloads. */
const STORAGE_KEY = 'seg-crm-relationship-tags-catalogue-v3';

function cloneCategories(
  categories: InsightTagCategory[]
): InsightTagCategory[] {
  return structuredClone(categories);
}

export function summariseCategories(
  categories: InsightTagCategory[]
): InsightTagsSummary {
  let tags = 0;
  let turned_on = 0;
  let turned_off = 0;

  for (const category of categories) {
    for (const tag of category.tags) {
      tags += 1;
      if (tag.is_active) turned_on += 1;
      else turned_off += 1;
    }
  }

  return {
    tags,
    categories: categories.length,
    turned_on,
    turned_off
  };
}

export function buildCatalogue(
  categories: InsightTagCategory[]
): InsightTagsCatalogue {
  return {
    categories,
    summary: summariseCategories(categories)
  };
}

function readStore(): InsightTagCategory[] {
  if (typeof sessionStorage === 'undefined') {
    return cloneCategories(defaultInsightTagCategories);
  }
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const seed = cloneCategories(defaultInsightTagCategories);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
  try {
    return JSON.parse(raw) as InsightTagCategory[];
  } catch {
    const seed = cloneCategories(defaultInsightTagCategories);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
}

function writeStore(categories: InsightTagCategory[]): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

export function getFixtureCatalogue(): InsightTagsCatalogue {
  return buildCatalogue(readStore());
}

export function createFixtureCategory(
  payload: CreateCategoryPayload
): InsightTagsCatalogue {
  const categories = readStore();
  const id = `cat-${slugify(payload.name) || 'new'}-${Date.now().toString(36)}`;
  categories.push({
    id,
    name: payload.name.trim(),
    colour: payload.colour,
    tags: []
  });
  writeStore(categories);
  return buildCatalogue(categories);
}

export function updateFixtureCategory(
  categoryId: string,
  payload: UpdateCategoryPayload
): InsightTagsCatalogue {
  const categories = readStore();
  const category = categories.find((row) => row.id === categoryId);
  if (!category) throw new Error(`Category ${categoryId} not found`);
  category.name = payload.name.trim();
  category.colour = payload.colour;
  writeStore(categories);
  return buildCatalogue(categories);
}

export function deleteFixtureCategory(categoryId: string): InsightTagsCatalogue {
  const categories = readStore();
  const category = categories.find((row) => row.id === categoryId);
  if (!category) throw new Error(`Category ${categoryId} not found`);
  if (category.tags.length > 0) {
    throw new Error(
      'Cannot delete category because it has active tags attached.'
    );
  }
  writeStore(categories.filter((row) => row.id !== categoryId));
  return buildCatalogue(readStore());
}

export function createFixtureTag(
  payload: CreateTagPayload
): InsightTagsCatalogue {
  const categories = readStore();
  const category = categories.find((row) => row.id === payload.category_id);
  if (!category) throw new Error(`Category ${payload.category_id} not found`);
  const id = `tag-${slugify(payload.name) || 'new'}-${Date.now().toString(36)}`;
  category.tags.push({
    id,
    name: payload.name.trim(),
    description: payload.description.trim(),
    is_active: true,
    times_applied: 0,
    last_applied_at: null
  });
  writeStore(categories);
  return buildCatalogue(categories);
}

export function renameFixtureTag(
  tagId: string,
  payload: RenameTagPayload
): InsightTagsCatalogue {
  const categories = readStore();
  for (const category of categories) {
    const tag = category.tags.find((row) => row.id === tagId);
    if (tag) {
      tag.name = payload.name.trim();
      writeStore(categories);
      return buildCatalogue(categories);
    }
  }
  throw new Error(`Tag ${tagId} not found`);
}

export function toggleFixtureTag(
  tagId: string,
  is_active: boolean
): InsightTagsCatalogue {
  const categories = readStore();
  for (const category of categories) {
    const tag = category.tags.find((row) => row.id === tagId);
    if (tag) {
      tag.is_active = is_active;
      writeStore(categories);
      return buildCatalogue(categories);
    }
  }
  throw new Error(`Tag ${tagId} not found`);
}

export function deleteFixtureTag(tagId: string): InsightTagsCatalogue {
  const categories = readStore();
  let removed = false;
  for (const category of categories) {
    const nextTags = category.tags.filter((row) => row.id !== tagId);
    if (nextTags.length !== category.tags.length) {
      category.tags = nextTags;
      removed = true;
      break;
    }
  }
  if (!removed) throw new Error(`Tag ${tagId} not found`);
  writeStore(categories);
  return buildCatalogue(categories);
}

export function resetFixtureCatalogue(): InsightTagsCatalogue {
  const seed = cloneCategories(defaultInsightTagCategories);
  writeStore(seed);
  return buildCatalogue(seed);
}
